import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { daysUntilDateOnly } from '@/lib/date-utils';
import { nomeDoEvento } from '@/lib/evento-nome';

export interface EventoDetalhes {
  id: string;
  nome_noiva: string;
  nome_noivo: string;
  data_evento: string;
  local_evento: string;
  numero_convidados: number;
  orcamento_total: number;
  valor_gasto: number;
  tipo_evento: string;
  hora_cerimonia?: string | null;
  mensagem_rsvp?: string | null;
  status?: string;
  created_at: string;
  updated_at: string;
  usuario_id: string;
  empresa_id: string;
  // Campos computados
  diasRestantes?: number;
  nomeCompleto?: string;
  clientes?: Array<{
    id: string;
    nome: string;
    papel: string;
    email?: string;
    telefone?: string;
  }>;
}

export function useEvento(eventoId: string | undefined) {
  const { user } = useAuth();

  const query = useQuery<EventoDetalhes | null>({
    queryKey: ['evento', eventoId, user?.id],
    enabled: !!eventoId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('eventos')
        .select(`
          *,
          evento_clientes(
            cliente_id,
            papel,
            clientes(id, nome, email, telefone)
          )
        `)
        .eq('id', eventoId!)
        .eq('usuario_id', user!.id)
        .single();

      if (error) {
        console.error('Erro ao carregar evento:', error);
        toast.error('Erro ao carregar dados do evento');
        throw error;
      }
      if (!data) return null;

      const diasRestantes = daysUntilDateOnly(data.data_evento) ?? 0;

      const clientes =
        (data as any).evento_clientes?.map((ec: any) => ({
          id: ec.clientes.id,
          nome: ec.clientes.nome,
          papel: ec.papel,
          email: ec.clientes.email,
          telefone: ec.clientes.telefone,
        })) || [];

      const nomeCompleto = nomeDoEvento({ ...(data as any), clientes });

      const eventoComputado: EventoDetalhes = {
        ...(data as any),
        diasRestantes,
        nomeCompleto,
        clientes,
      };

      return eventoComputado;
    },
  });

  return {
    evento: query.data ?? null,
    loading: query.isLoading,
    error: query.error ? 'Erro ao carregar evento' : null,
    refetch: query.refetch,
  };
}
